import { useState, useEffect } from 'react';
import api from '../utils/api';
import {
  Plus, PencilSimple, Trash, X, FloppyDisk, ArrowsClockwise, ChartBar,
  GitBranch, ToggleLeft, EnvelopeSimple, Buildings, ArrowSquareOut,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { COMPLAINT_GROUPS, COMPLAINT_GROUP_LABEL, DEPT_OPTIONS_BY_ENTITY, ALL_DEPT_OPTIONS } from '../constants/ticketOptions';
import { Toggle } from '../components/redesign/ui';
import AdminPanelView from './AdminPanel.view';

// ── Constants ──────────────────────────────────────────────────────────────────

// Stats bucket key → display label (D4: in_progress bucket shows 'Under Process';
// pending_third_party bucket holds pending_finance tickets → 'Pending Finance').
const STAT_BUCKET_LABELS = {
  new:                 'New',
  escalated:           'Escalated',
  in_progress:         'Under Process',
  pending_third_party: 'Pending Finance',
  resolved:            'Resolved',
  closed:              'Closed',
};

const PRIORITY_OPTIONS = [
  { value: '',         label: '— No override —' },
  { value: 'low',      label: 'Low' },
  { value: 'medium',   label: 'Medium' },
  { value: 'high',     label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const EMPTY_FORM = {
  complaint_group: '',
  assigned_to: '',
  department: '',
  priority_override: '',
  is_fallback: false,
  is_active: true,
};

// Shared token-styled input/select class.
const INPUT = 'w-full rounded-field border border-paper-border2 bg-paper px-3 py-2 text-sm text-ink outline-none focus:border-ink';
// Modal overlay + card shells — established Users/Flows/Offers pattern.
const OVERLAY = 'fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm';
const MODAL_CARD = 'w-full rounded-card border border-paper-border bg-paper-panel shadow-soft';

// ── Routing Rules Tab ──────────────────────────────────────────────────────────

function RoutingRulesTab({ entities }) {
  const [selectedEntity, setSelectedEntity] = useState(entities[0]?.slug || '');
  const [rules, setRules]       = useState([]);
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editRule, setEditRule]  = useState(null);
  const [form, setForm]          = useState(EMPTY_FORM);
  const [saving, setSaving]      = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const deptOptions = DEPT_OPTIONS_BY_ENTITY[selectedEntity] || ALL_DEPT_OPTIONS;

  const loadRules = async (slug) => {
    if (!slug) return;
    setLoading(true);
    try {
      const [rulesRes, usersRes] = await Promise.all([
        api.get(`/routing-rules?entity_slug=${slug}`),
        api.get('/users'),
      ]);
      setRules(rulesRes.data);
      setUsers(usersRes.data.filter((u) => u.is_active !== false));
    } catch {
      toast.error('Failed to load routing rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedEntity) loadRules(selectedEntity);
  }, [selectedEntity]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => { setEditRule(null); setForm({ ...EMPTY_FORM }); setShowModal(true); };
  const openEdit = (rule) => {
    setEditRule(rule);
    setForm({
      complaint_group:   rule.is_fallback ? '' : rule.complaint_group,
      assigned_to:       rule.assigned_to  || '',
      department:        rule.department   || '',
      priority_override: rule.priority_override || '',
      is_fallback:       rule.is_fallback,
      is_active:         rule.is_active,
    });
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditRule(null); };

  const saveRule = async () => {
    if (!form.is_fallback && !form.complaint_group) {
      toast.error('Select a complaint group or mark as Catch-all Fallback'); return;
    }
    if (!form.assigned_to && !form.department && !form.priority_override) {
      toast.error('Set at least one action: Assigned To, Department, or Priority Override'); return;
    }
    setSaving(true);
    try {
      const payload = {
        entity_slug:       selectedEntity,
        complaint_group:   form.is_fallback ? '__fallback__' : form.complaint_group,
        assigned_to:       form.assigned_to || null,
        department:        form.department  || null,
        priority_override: form.priority_override || null,
        is_fallback:       form.is_fallback,
        is_active:         form.is_active,
      };
      if (editRule) { await api.put(`/routing-rules/${editRule.id}`, payload); toast.success('Rule updated'); }
      else          { await api.post('/routing-rules', payload);              toast.success('Rule created'); }
      closeModal();
      loadRules(selectedEntity);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save rule');
    } finally { setSaving(false); }
  };

  const deleteRule = async (id) => {
    try {
      await api.delete(`/routing-rules/${id}`);
      toast.success('Rule deleted');
      setDeleteConfirm(null);
      loadRules(selectedEntity);
    } catch { toast.error('Failed to delete rule'); }
  };

  const groupLabel = (group, isFallback) => {
    if (isFallback) return <span className="italic text-ink-faint">Catch-all (Fallback)</span>;
    return COMPLAINT_GROUP_LABEL[group] || group;
  };
  const priorityTone = (p) => {
    if (!p) return '';
    return p === 'critical' ? 'font-semibold text-status-escalated'
      : p === 'high'    ? 'font-semibold text-status-pending-finance'
      : p === 'medium'  ? 'text-status-pending-finance'
      : 'text-status-resolved';
  };

  const entityUsers = users.filter((u) => {
    if (u.is_active === false) return false;
    const entity = entities.find((e) => e.slug === selectedEntity);
    if (!entity) return false;
    if (u.entity_id === entity.id) return true;
    if (Array.isArray(u.entities) && u.entities.includes(selectedEntity)) return true;
    return false;
  });

  return (
    <div className="flex flex-col gap-5">
      {/* Entity selector + Add button */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-ink-muted">Entity:</label>
          <select value={selectedEntity} onChange={(e) => setSelectedEntity(e.target.value)}
            className="rounded-field border border-paper-border2 bg-paper px-3 py-1.5 text-sm text-ink outline-none focus:border-ink">
            {entities.map((e) => <option key={e.slug} value={e.slug}>{e.name}</option>)}
          </select>
        </div>
        <button onClick={openAdd}
          className="inline-flex items-center gap-2 rounded-field bg-ink px-4 py-2 text-sm font-semibold text-paper hover:bg-ink/90">
          <Plus size={14} weight="bold" />Add Rule
        </button>
      </div>

      {/* Rules table */}
      <div className="overflow-hidden rounded-card border border-paper-border bg-paper-panel shadow-soft">
        {loading ? (
          <div className="flex justify-center py-12"><ArrowsClockwise size={24} className="animate-spin text-status-under-process" /></div>
        ) : rules.length === 0 ? (
          <div className="py-12 text-center text-sm text-ink-faint">
            No routing rules for this entity. Click <strong className="text-ink">Add Rule</strong> to create one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-paper-border bg-paper-muted/40">
                {['Complaint Group','Assigned To','Department','Priority Override','Fallback','Active'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-faint">{h}</th>
                ))}
                <th className="w-20 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} className="border-b border-paper-border transition-colors hover:bg-paper-muted/50">
                  <td className="px-4 py-3 font-medium text-ink">{groupLabel(rule.complaint_group, rule.is_fallback)}</td>
                  <td className="px-4 py-3 text-ink-muted">{rule.assigned_to_name || '—'}</td>
                  <td className="px-4 py-3 text-ink-muted">
                    {rule.department
                      ? ALL_DEPT_OPTIONS.find((d) => d.slug === rule.department)?.label || rule.department
                      : '—'}
                  </td>
                  <td className={`px-4 py-3 capitalize ${priorityTone(rule.priority_override)}`}>
                    {rule.priority_override || '—'}
                  </td>
                  <td className="px-4 py-3">
                    {rule.is_fallback
                      ? <span className="rounded-chip bg-status-under-process/12 px-2 py-0.5 text-xs font-medium text-status-under-process">Yes</span>
                      : <span className="text-xs text-ink-faint">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {rule.is_active
                      ? <span className="rounded-chip bg-status-resolved/12 px-2 py-0.5 text-xs font-medium text-status-resolved">Active</span>
                      : <span className="rounded-chip bg-paper-muted px-2 py-0.5 text-xs font-medium text-ink-muted">Inactive</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(rule)} title="Edit"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-field text-ink-faint transition hover:bg-paper-muted hover:text-status-under-process">
                        <PencilSimple size={13} />
                      </button>
                      <button onClick={() => setDeleteConfirm(rule)} title="Delete"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-field text-ink-faint transition hover:bg-paper-muted hover:text-status-escalated">
                        <Trash size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className={OVERLAY}>
          <div className={`${MODAL_CARD} max-w-md`}>
            <div className="flex items-center justify-between border-b border-paper-border px-6 py-4">
              <h2 className="font-serif text-base text-ink">{editRule ? 'Edit Routing Rule' : 'Add Routing Rule'}</h2>
              <button onClick={closeModal} className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-muted hover:bg-paper-muted">
                <X size={16} />
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              {/* Fallback toggle */}
              <div className="flex items-center justify-between rounded-field border border-status-under-process/30 bg-status-under-process/8 p-3">
                <div>
                  <p className="text-sm font-medium text-status-under-process">Catch-all Fallback</p>
                  <p className="mt-0.5 text-xs text-ink-muted">Applies when no specific rule matches</p>
                </div>
                <button onClick={() => setForm((f) => ({ ...f, is_fallback: !f.is_fallback, complaint_group: '' }))}
                  className={`relative h-6 w-11 rounded-full transition-colors ${form.is_fallback ? 'bg-status-under-process' : 'bg-paper-border2'}`}>
                  <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-paper shadow transition-transform ${form.is_fallback ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              {/* Complaint Group */}
              <div>
                <label className="mb-1 block text-xs text-ink-faint">
                  Complaint Group {!form.is_fallback && <span className="text-status-escalated">*</span>}
                </label>
                <select value={form.complaint_group} onChange={(e) => setForm((f) => ({ ...f, complaint_group: e.target.value }))}
                  disabled={form.is_fallback} className={`${INPUT} disabled:bg-paper-muted disabled:text-ink-faint`}>
                  <option value="">— Select group —</option>
                  {COMPLAINT_GROUPS.map((g) => <option key={g.slug} value={g.slug}>{g.label}</option>)}
                </select>
              </div>

              {/* Assigned To */}
              <div>
                <label className="mb-1 block text-xs text-ink-faint">Assigned To</label>
                <select value={form.assigned_to} onChange={(e) => setForm((f) => ({ ...f, assigned_to: e.target.value }))} className={INPUT}>
                  <option value="">— No assignment —</option>
                  {entityUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}{u.department ? ` (${u.department})` : ''}</option>
                  ))}
                </select>
              </div>

              {/* Department */}
              <div>
                <label className="mb-1 block text-xs text-ink-faint">Route to Department</label>
                <select value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} className={INPUT}>
                  <option value="">— No department routing —</option>
                  {deptOptions.map((d) => <option key={d.slug} value={d.slug}>{d.label}</option>)}
                </select>
                {form.department && (
                  <p className="mt-1 text-xs text-status-pending-finance">⚠️ Status will change to Escalated when department is set</p>
                )}
              </div>

              {/* Priority Override */}
              <div>
                <label className="mb-1 block text-xs text-ink-faint">Priority Override</label>
                <select value={form.priority_override} onChange={(e) => setForm((f) => ({ ...f, priority_override: e.target.value }))} className={INPUT}>
                  {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-ink-muted">Rule Active</label>
                <button onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
                  className={`relative h-6 w-11 rounded-full transition-colors ${form.is_active ? 'bg-status-resolved' : 'bg-paper-border2'}`}>
                  <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-paper shadow transition-transform ${form.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-paper-border px-6 py-4">
              <button onClick={closeModal} className="rounded-field px-4 py-2 text-sm text-ink-muted hover:bg-paper-muted">Cancel</button>
              <button onClick={saveRule} disabled={saving}
                className="inline-flex items-center gap-2 rounded-field bg-ink px-5 py-2 text-sm font-semibold text-paper hover:bg-ink/90 disabled:opacity-50">
                <FloppyDisk size={14} weight="fill" />
                {saving ? 'Saving…' : editRule ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className={OVERLAY}>
          <div className={`${MODAL_CARD} max-w-sm p-6`}>
            <h3 className="mb-2 font-serif text-base text-ink">Delete Routing Rule</h3>
            <p className="mb-5 text-sm text-ink-muted">
              Are you sure you want to delete the rule for{' '}
              <strong className="text-ink">{deleteConfirm.is_fallback ? 'Catch-all Fallback' : deleteConfirm.complaint_group}</strong>?
              This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="rounded-field px-4 py-2 text-sm text-ink-muted hover:bg-paper-muted">Cancel</button>
              <button onClick={() => deleteRule(deleteConfirm.id)}
                className="rounded-field bg-status-escalated px-4 py-2 text-sm font-semibold text-paper hover:bg-status-escalated/90">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Email-list card shells ────────────────────────────────────────────────────
// Four card components share a near-identical shape; each keeps its own state
// + fetch to preserve the exact endpoints. Only the visual chrome is shared.

const EMAIL_RE_FE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMAIL_CARD_SHELL = 'rounded-card border border-paper-border bg-paper-panel p-5 shadow-soft';
const EMAIL_ROW = 'flex items-center gap-2 rounded-field border border-paper-border bg-paper px-3 py-1.5';
const EMAIL_TEXT = 'flex-1 font-mono text-[13px] text-ink';
const REMOVE_BTN = 'text-status-escalated hover:text-status-escalated/70 font-bold text-lg leading-none px-1';
const REMOVE_BTN_DISABLED = 'cursor-not-allowed text-ink-faint font-bold text-lg leading-none px-1';
const SAVE_BTN = 'inline-flex items-center gap-1.5 rounded-field bg-status-resolved px-4 py-1.5 text-sm font-medium text-paper hover:bg-status-resolved/90 disabled:opacity-60';
const ADD_BTN = 'rounded-field bg-status-under-process px-4 py-1.5 text-sm font-medium text-paper hover:bg-status-under-process/90 disabled:cursor-not-allowed disabled:opacity-50';

function TestEmailsCard({ initialEmails }) {
  const [emails, setEmails]     = useState(initialEmails || []);
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState(null);

  const addEmail = () => {
    const val = inputVal.trim().toLowerCase();
    if (!val) return;
    if (!EMAIL_RE_FE.test(val)) { setFeedback({ ok: false, msg: 'Invalid email format.' }); return; }
    if (emails.includes(val))   { setFeedback({ ok: false, msg: 'Already in the list.' }); return; }
    if (emails.length >= 10)    { setFeedback({ ok: false, msg: 'Maximum 10 emails allowed.' }); return; }
    setEmails((prev) => [...prev, val]);
    setInputVal(''); setFeedback(null);
  };
  const removeEmail = (email) => {
    if (emails.length <= 1) return;
    setEmails((prev) => prev.filter((e) => e !== email));
    setFeedback(null);
  };
  const save = async () => {
    setSaving(true); setFeedback(null);
    try {
      const r = await fetch('/api/settings/test-emails', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ emails }),
      });
      const data = await r.json();
      if (!r.ok) { setFeedback({ ok: false, msg: data.error || 'Save failed.' }); return; }
      setEmails(data.test_emails);
      setFeedback({ ok: true, msg: 'Saved.' });
    } catch { setFeedback({ ok: false, msg: 'Network error — try again.' }); }
    finally { setSaving(false); }
  };

  return (
    <div className={EMAIL_CARD_SHELL}>
      <div className="mb-1 font-serif text-base text-ink">Test Notification Emails</div>
      <p className="mb-4 text-xs text-ink-muted">
        While an entity is in TEST mode, all its notification emails are redirected to these addresses instead of the real team.
      </p>
      <div className="mb-4 flex flex-col gap-1.5">
        {emails.map((email) => (
          <div key={email} className={EMAIL_ROW}>
            <span className={EMAIL_TEXT}>{email}</span>
            <button onClick={() => removeEmail(email)} disabled={emails.length <= 1}
              title={emails.length <= 1 ? 'Cannot remove the last email' : 'Remove'}
              className={emails.length <= 1 ? REMOVE_BTN_DISABLED : REMOVE_BTN}>×</button>
          </div>
        ))}
      </div>
      <div className="mb-3 flex gap-2">
        <input type="email" value={inputVal}
          onChange={(e) => { setInputVal(e.target.value); setFeedback(null); }}
          onKeyDown={(e) => e.key === 'Enter' && addEmail()}
          placeholder="new@example.com" className={INPUT} />
        <button onClick={addEmail} disabled={emails.length >= 10} className={ADD_BTN}>Add</button>
      </div>
      {feedback && (
        <p className={`mb-2 text-xs ${feedback.ok ? 'text-status-resolved' : 'text-status-escalated'}`}>{feedback.msg}</p>
      )}
      <button onClick={save} disabled={saving} className={SAVE_BTN}>
        <FloppyDisk size={13} weight="fill" />{saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function AlertEmailsCard({ initialEmails }) {
  const [emails, setEmails]     = useState(initialEmails || []);
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState(null);

  const addEmail = () => {
    const val = inputVal.trim().toLowerCase();
    if (!val) return;
    if (!EMAIL_RE_FE.test(val)) { setFeedback({ ok: false, msg: 'Invalid email format.' }); return; }
    if (emails.includes(val))   { setFeedback({ ok: false, msg: 'Already in the list.' }); return; }
    if (emails.length >= 10)    { setFeedback({ ok: false, msg: 'Maximum 10 emails allowed.' }); return; }
    setEmails((prev) => [...prev, val]);
    setInputVal(''); setFeedback(null);
  };
  const removeEmail = (email) => { setEmails((prev) => prev.filter((e) => e !== email)); setFeedback(null); };
  const save = async () => {
    setSaving(true); setFeedback(null);
    try {
      const r = await fetch('/api/settings/alert-emails', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ emails }),
      });
      const data = await r.json();
      if (!r.ok) { setFeedback({ ok: false, msg: data.error || 'Save failed.' }); return; }
      setEmails(data.emails);
      setFeedback({ ok: true, msg: 'Saved.' });
    } catch { setFeedback({ ok: false, msg: 'Network error — try again.' }); }
    finally { setSaving(false); }
  };

  return (
    <div className={EMAIL_CARD_SHELL}>
      <div className="mb-1 font-serif text-base text-ink">Alert Notifications</div>
      <p className="mb-4 text-xs text-ink-muted">
        When the email agent fails to process an incoming email, n8n sends an alert to these addresses.
        Empty list = no alert emails.
      </p>
      <div className="mb-4 flex flex-col gap-1.5">
        {emails.length === 0 && <p className="text-xs italic text-ink-faint">No alert recipients configured.</p>}
        {emails.map((email) => (
          <div key={email} className={EMAIL_ROW}>
            <span className={EMAIL_TEXT}>{email}</span>
            <button onClick={() => removeEmail(email)} title="Remove" className={REMOVE_BTN}>×</button>
          </div>
        ))}
      </div>
      <div className="mb-3 flex gap-2">
        <input type="email" value={inputVal}
          onChange={(e) => { setInputVal(e.target.value); setFeedback(null); }}
          onKeyDown={(e) => e.key === 'Enter' && addEmail()}
          placeholder="alerts@example.com" className={INPUT} />
        <button onClick={addEmail} disabled={emails.length >= 10} className={ADD_BTN}>Add</button>
      </div>
      {feedback && <p className={`mb-2 text-xs ${feedback.ok ? 'text-status-resolved' : 'text-status-escalated'}`}>{feedback.msg}</p>}
      <button onClick={save} disabled={saving} className={SAVE_BTN}>
        <FloppyDisk size={13} weight="fill" />{saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function HrEmailsCard({ initialEmails }) {
  const [emails, setEmails]     = useState(initialEmails || []);
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState(null);

  const addEmail = () => {
    const val = inputVal.trim().toLowerCase();
    if (!val) return;
    if (!EMAIL_RE_FE.test(val)) { setFeedback({ ok: false, msg: 'Invalid email format.' }); return; }
    if (emails.includes(val))   { setFeedback({ ok: false, msg: 'Already in the list.' }); return; }
    if (emails.length >= 10)    { setFeedback({ ok: false, msg: 'Maximum 10 emails allowed.' }); return; }
    setEmails((prev) => [...prev, val]);
    setInputVal(''); setFeedback(null);
  };
  const removeEmail = (email) => { setEmails((prev) => prev.filter((e) => e !== email)); setFeedback(null); };
  const save = async () => {
    setSaving(true); setFeedback(null);
    try {
      const r = await fetch('/api/settings/hr-emails', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ emails }),
      });
      const data = await r.json();
      if (!r.ok) { setFeedback({ ok: false, msg: data.error || 'Save failed.' }); return; }
      setEmails(data.emails);
      setFeedback({ ok: true, msg: 'Saved.' });
    } catch { setFeedback({ ok: false, msg: 'Network error — try again.' }); }
    finally { setSaving(false); }
  };

  return (
    <div className={EMAIL_CARD_SHELL}>
      <div className="mb-1 font-serif text-base text-ink">HR Notifications</div>
      <p className="mb-1 text-xs text-ink-muted">
        Job applications and CVs sent to customer care are auto-detected and forwarded to these
        addresses. Leave empty to disable HR forwarding.
      </p>
      <p className="mb-4 text-[11px] text-ink-faint">Test mode still redirects these like every other notification.</p>
      <div className="mb-4 flex flex-col gap-1.5">
        {emails.length === 0 && <p className="text-xs italic text-ink-faint">No HR recipients configured — HR forwarding is off.</p>}
        {emails.map((email) => (
          <div key={email} className={EMAIL_ROW}>
            <span className={EMAIL_TEXT}>{email}</span>
            <button onClick={() => removeEmail(email)} title="Remove" className={REMOVE_BTN}>×</button>
          </div>
        ))}
      </div>
      <div className="mb-3 flex gap-2">
        <input type="email" value={inputVal}
          onChange={(e) => { setInputVal(e.target.value); setFeedback(null); }}
          onKeyDown={(e) => e.key === 'Enter' && addEmail()}
          placeholder="hr@example.com" className={INPUT} />
        <button onClick={addEmail} disabled={emails.length >= 10} className={ADD_BTN}>Add</button>
      </div>
      {feedback && <p className={`mb-2 text-xs ${feedback.ok ? 'text-status-resolved' : 'text-status-escalated'}`}>{feedback.msg}</p>}
      <button onClick={save} disabled={saving} className={SAVE_BTN}>
        <FloppyDisk size={13} weight="fill" />{saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function DeptEmailsCard({ initialMap }) {
  const [map, setMap]           = useState(initialMap || {});
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState(null);

  const setDept = (slug, value) => { setMap((prev) => ({ ...prev, [slug]: value })); setFeedback(null); };

  const save = async () => {
    const invalid = Object.entries(map)
      .filter(([, v]) => String(v || '').trim() && !EMAIL_RE_FE.test(String(v).trim().toLowerCase()))
      .map(([slug]) => slug);
    if (invalid.length) { setFeedback({ ok: false, msg: `Invalid email for: ${invalid.join(', ')}` }); return; }

    setSaving(true); setFeedback(null);
    try {
      const r = await fetch('/api/settings/dept-emails', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ dept_emails: map }),
      });
      const data = await r.json();
      if (!r.ok) { setFeedback({ ok: false, msg: data.error || 'Save failed.' }); return; }
      setMap(data.dept_emails);
      setFeedback({ ok: true, msg: 'Saved.' });
    } catch { setFeedback({ ok: false, msg: 'Network error — try again.' }); }
    finally { setSaving(false); }
  };

  return (
    <div className={EMAIL_CARD_SHELL}>
      <div className="mb-1 font-serif text-base text-ink">Department Notifications</div>
      <p className="mb-4 text-xs text-ink-muted">
        Live email address per department — added as a recipient when a ticket is routed to that department.
        Leave empty for no department recipient (CS default only). Test mode redirects these like every other notification.
      </p>
      <div className="mb-4 flex flex-col gap-2">
        {ALL_DEPT_OPTIONS.map((d) => (
          <div key={d.slug} className="flex items-center gap-2.5">
            <span className="w-40 flex-none text-sm text-ink-muted">{d.label}</span>
            <input type="email" value={map[d.slug] || ''}
              onChange={(e) => setDept(d.slug, e.target.value)}
              placeholder="— no recipient —"
              className={`${INPUT} font-mono text-[13px]`} />
          </div>
        ))}
      </div>
      {feedback && <p className={`mb-2 text-xs ${feedback.ok ? 'text-status-resolved' : 'text-status-escalated'}`}>{feedback.msg}</p>}
      <button onClick={save} disabled={saving} className={SAVE_BTN}>
        <FloppyDisk size={13} weight="fill" />{saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

// ── System Mode Tab ───────────────────────────────────────────────────────────

// Reusable mode-toggle card (tokenized rewrite of live's inline-styled ModeCard).
function ModeCard({
  title, subtitle, live, loading,
  liveDescription, testDescription,
  onGoLive, onGoTest,
  showConfirm, confirmTitle, confirmBody,
  onConfirm, onCancelConfirm,
}) {
  const shellCls = live
    ? 'flex-1 rounded-card border border-status-resolved/40 bg-status-resolved/5 p-5'
    : 'flex-1 rounded-card border border-status-pending-finance/40 bg-status-pending-finance/5 p-5';
  const chipCls = live
    ? 'inline-flex items-center gap-1.5 rounded-chip bg-status-resolved/12 px-2.5 py-0.5 text-[11px] font-semibold text-status-resolved'
    : 'inline-flex items-center gap-1.5 rounded-chip bg-status-pending-finance/14 px-2.5 py-0.5 text-[11px] font-semibold text-status-pending-finance';
  const dotCls = live ? 'bg-status-resolved' : 'bg-status-pending-finance';
  const buttonCls = live
    ? 'rounded-field bg-status-pending-finance px-4 py-1.5 text-sm font-medium text-paper hover:bg-status-pending-finance/90 disabled:opacity-60'
    : 'rounded-field bg-status-resolved px-4 py-1.5 text-sm font-medium text-paper hover:bg-status-resolved/90 disabled:opacity-60';

  return (
    <div className={`${shellCls} min-w-[260px]`}>
      <div className="mb-2.5 flex items-start justify-between">
        <div>
          <div className="text-sm font-semibold text-ink">{title}</div>
          <div className="mt-0.5 text-xs text-ink-muted">{subtitle}</div>
        </div>
        <span className={chipCls}>
          <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
          {live ? 'LIVE' : 'TEST'}
        </span>
      </div>
      <p className="mb-3.5 text-xs text-ink-muted">{live ? liveDescription : testDescription}</p>
      <div className="flex items-center gap-3">
        <button onClick={live ? onGoTest : onGoLive} disabled={loading || live === null} className={buttonCls}>
          {loading ? 'Switching…' : live ? 'Switch to TEST' : 'Switch to LIVE'}
        </button>
        <button onClick={live ? onGoTest : onGoLive} disabled={loading || live === null}
          title={live ? 'Switch to TEST' : 'Switch to LIVE'} className="disabled:opacity-60">
          <Toggle on={live} />
        </button>
      </div>
      {showConfirm && (
        <div className="mt-3.5 rounded-field border border-status-escalated/30 bg-status-escalated/8 p-3.5">
          <p className="mb-1.5 text-sm font-semibold text-status-escalated">⚠ {confirmTitle}</p>
          <p className="mb-3 text-xs text-status-escalated/80">{confirmBody}</p>
          <div className="flex gap-2">
            <button onClick={onConfirm}
              className="rounded-field bg-status-escalated px-3.5 py-1.5 text-xs font-semibold text-paper hover:bg-status-escalated/90">
              Yes, Go Live
            </button>
            <button onClick={onCancelConfirm}
              className="rounded-field bg-paper-muted px-3.5 py-1.5 text-xs text-ink-muted hover:bg-paper-border2">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SystemModeTab() {
  const [abraajLive, setAbraajLive]           = useState(null);
  const [lofatLive, setLofatLive]             = useState(null);
  const [igAbraajLive, setIgAbraajLive]       = useState(null);
  const [testEmails, setTestEmails]           = useState(null);
  const [alertEmails, setAlertEmails]         = useState(null);
  const [deptEmails, setDeptEmails]           = useState(null);
  const [hrEmails, setHrEmails]               = useState(null);
  const [abraajLoading, setAbraajLoading]     = useState(false);
  const [lofatLoading, setLofatLoading]       = useState(false);
  const [igAbraajLoading, setIgAbraajLoading] = useState(false);
  const [showAbraajConfirm, setShowAbraajConfirm]     = useState(false);
  const [showLofatConfirm, setShowLofatConfirm]       = useState(false);
  const [showIgAbraajConfirm, setShowIgAbraajConfirm] = useState(false);

  useEffect(() => {
    fetch('/api/settings/mode')
      .then((r) => r.json())
      .then((data) => {
        setAbraajLive(data.abraaj_live_mode);
        setLofatLive(data.lofat_live_mode);
        setIgAbraajLive(data.instagram_abraaj_live_mode);
        setTestEmails(data.test_emails || ['a.alnajdi@aralsane.com']);
      })
      .catch(() => toast.error('Failed to load mode settings'));
    fetch('/api/settings/alert-emails').then((r) => r.json()).then((d) => setAlertEmails(d.emails || [])).catch(() => setAlertEmails([]));
    fetch('/api/settings/dept-emails').then((r) => r.json()).then((d) => setDeptEmails(d.dept_emails || {})).catch(() => setDeptEmails({}));
    fetch('/api/settings/hr-emails').then((r) => r.json()).then((d) => setHrEmails(d.emails || [])).catch(() => setHrEmails([]));
  }, []);

  const applyMode = (endpoint, goLive, setter, setLoading) => {
    setLoading(true);
    fetch(`/api/settings/${endpoint}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ live: goLive }),
    })
      .then((r) => r.json())
      .then(() => {
        setter(goLive);
        window.location.reload();
      })
      .catch(() => toast.error('Failed to update mode. Try again.'))
      .finally(() => setLoading(false));
  };

  if (abraajLive === null || testEmails === null || alertEmails === null || deptEmails === null || hrEmails === null) {
    return (
      <div className="flex justify-center py-12"><ArrowsClockwise size={24} className="animate-spin text-status-under-process" /></div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-1 font-serif text-base text-ink">System Mode</h3>
        <p className="text-[13px] text-ink-muted">
          Each entity and agent is controlled independently. Go live on Abraaj without affecting Lofat.
          Enable Instagram only when Meta App Review is approved.
        </p>
      </div>

      {/* Row 1 — Portal / Email toggles */}
      <div className="flex flex-wrap gap-4">
        <ModeCard
          title="Abraaj Water — Portal & Email"
          subtitle="Ticket notification emails · abraajwater.com"
          live={abraajLive} loading={abraajLoading}
          liveDescription="Emails are being sent to real Abraaj team members."
          testDescription="All Abraaj notification emails are redirected to a.alnajdi@aralsane.com."
          onGoLive={() => setShowAbraajConfirm(true)}
          onGoTest={() => applyMode('abraaj-mode', false, setAbraajLive, setAbraajLoading)}
          showConfirm={showAbraajConfirm}
          confirmTitle="Switch Abraaj to LIVE?"
          confirmBody="Real notification emails will be sent to Ibrahim, Rawan, and Antony immediately."
          onConfirm={() => { setShowAbraajConfirm(false); applyMode('abraaj-mode', true, setAbraajLive, setAbraajLoading); }}
          onCancelConfirm={() => setShowAbraajConfirm(false)}
        />
        <ModeCard
          title="Lofat Lifestyle — Portal & Email"
          subtitle="Ticket notification emails · lofateats.com"
          live={lofatLive} loading={lofatLoading}
          liveDescription="Emails are being sent to real Lofat team members."
          testDescription="All Lofat notification emails are redirected to a.alnajdi@aralsane.com."
          onGoLive={() => setShowLofatConfirm(true)}
          onGoTest={() => applyMode('lofat-mode', false, setLofatLive, setLofatLoading)}
          showConfirm={showLofatConfirm}
          confirmTitle="Switch Lofat to LIVE?"
          confirmBody="Real notification emails will be sent to Lofat team members immediately. Confirm the Lofat OPTIMIZE phase is complete before going live."
          onConfirm={() => { setShowLofatConfirm(false); applyMode('lofat-mode', true, setLofatLive, setLofatLoading); }}
          onCancelConfirm={() => setShowLofatConfirm(false)}
        />
      </div>

      {/* Row 2 — Instagram */}
      <div className="flex flex-wrap gap-4">
        <ModeCard
          title="Instagram Agent — @abraajwater"
          subtitle="DM auto-responses · Abraaj Water"
          live={igAbraajLive} loading={igAbraajLoading}
          liveDescription="Agent is responding to all real customer DMs on @abraajwater."
          testDescription="Agent only responds to test accounts. Real customer DMs are ignored."
          onGoLive={() => setShowIgAbraajConfirm(true)}
          onGoTest={() => applyMode('instagram-abraaj-mode', false, setIgAbraajLive, setIgAbraajLoading)}
          showConfirm={showIgAbraajConfirm}
          confirmTitle="Switch Instagram Agent to LIVE?"
          confirmBody="⚠ Requires Meta App Review approval AND TEST_SENDER_ID_PLACEHOLDER replaced with real sender IDs in n8n before enabling. Do not enable before both steps are complete."
          onConfirm={() => { setShowIgAbraajConfirm(false); applyMode('instagram-abraaj-mode', true, setIgAbraajLive, setIgAbraajLoading); }}
          onCancelConfirm={() => setShowIgAbraajConfirm(false)}
        />
      </div>

      <TestEmailsCard initialEmails={testEmails} />
      <AlertEmailsCard initialEmails={alertEmails} />
      <DeptEmailsCard initialMap={deptEmails} />
      <HrEmailsCard initialEmails={hrEmails} />
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab({ entities, stats }) {
  const [modeData, setModeData] = useState(null);

  useEffect(() => {
    fetch('/api/settings/mode').then((r) => r.json()).then(setModeData).catch(() => {});
  }, []);

  const ModeBadge = ({ slug }) => {
    if (!modeData) return null;
    let live = null;
    if (slug === 'abraaj-water')         live = modeData.abraaj_live_mode;
    else if (slug === 'lofat-lifestyle') live = modeData.lofat_live_mode;

    if (live === null) {
      return <span className="inline-flex rounded-chip bg-paper-muted px-2 py-0.5 text-[11px] font-semibold text-ink-muted">NOT CONFIGURED</span>;
    }
    return live
      ? <span className="inline-flex rounded-chip bg-status-resolved/12 px-2 py-0.5 text-[11px] font-semibold text-status-resolved">LIVE</span>
      : <span className="inline-flex rounded-chip bg-status-pending-finance/14 px-2 py-0.5 text-[11px] font-semibold text-status-pending-finance">TEST MODE</span>;
  };

  const entityCardCls = (slug) => {
    if (slug === 'abraaj-water')    return 'rounded-card border-2 border-entity-abraaj/30 bg-entity-abraaj/8 p-4';
    if (slug === 'lofat-lifestyle') return 'rounded-card border-2 border-entity-lofat/30 bg-entity-lofat/8 p-4';
    return 'rounded-card border-2 border-paper-border bg-paper-muted p-4';
  };

  return (
    <>
      <div className="rounded-card border border-paper-border bg-paper-panel p-5 shadow-soft">
        <h3 className="mb-4 flex items-center gap-2 font-serif text-base text-ink">
          <Buildings size={18} className="text-ink-muted" />
          Entities
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {entities.map((entity) => (
            <div key={entity.id} className={entityCardCls(entity.slug)}>
              <div className="font-semibold text-ink">{entity.name}</div>
              <div className="mt-0.5 text-xs text-ink-muted">{entity.slug}</div>
              <div className="mt-1 text-xs text-ink-faint">{entity.email_address}</div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <ModeBadge slug={entity.slug} />
                {!entity.is_active && (
                  <span className="inline-flex rounded-chip bg-status-escalated/12 px-2 py-0.5 text-[11px] font-semibold text-status-escalated">
                    Disabled
                  </span>
                )}
              </div>
              {entity.slug === 'abraaj-water' && modeData && (
                <div className={`mt-1.5 text-[11px] font-medium ${modeData.instagram_abraaj_live_mode ? 'text-status-resolved' : 'text-status-pending-finance'}`}>
                  Instagram: {modeData.instagram_abraaj_live_mode ? 'LIVE' : 'TEST'}
                </div>
              )}
              {entity.slug === 'regency-kuwait' && (
                <div className="mt-1 text-xs font-medium text-status-pending-finance">UI Coming Soon</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-card border border-paper-border bg-paper-panel p-5 shadow-soft">
        <h3 className="mb-4 flex items-center gap-2 font-serif text-base text-ink">
          <ChartBar size={18} className="text-ink-muted" />
          System Statistics
        </h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {stats?.byStatus?.map((s) => (
            <div key={s.status} className="rounded-field bg-paper-muted p-3 text-center">
              <div className="font-serif text-2xl text-ink">{s.count}</div>
              <div className="mt-1 text-xs text-ink-muted">{STAT_BUCKET_LABELS[s.status] || s.status?.replace(/_/g, ' ')}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-card border border-paper-border bg-paper-panel p-5 shadow-soft">
        <h3 className="mb-4 font-serif text-base text-ink">HubSpot Integration</h3>
        <div className="flex flex-col text-sm">
          <div className="flex justify-between border-b border-paper-border py-2">
            <span className="text-ink-muted">Abraaj Water Pipeline</span>
            <span className="font-mono text-ink">3765774551</span>
          </div>
          <div className="flex justify-between border-b border-paper-border py-2">
            <span className="text-ink-muted">Lofat Lifestyle Pipeline</span>
            <span className="font-mono text-ink">3765617862</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-ink-muted">Token Status</span>
            <span className="rounded-chip bg-status-pending-finance/12 px-2 py-0.5 text-xs font-medium text-status-pending-finance">
              Configure in .env
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Failed Emails Tab ─────────────────────────────────────────────────────────

const FAILED_EMAIL_STATUS_CFG = {
  open:      { label: 'Open',      cls: 'bg-status-pending-finance/12 text-status-pending-finance' },
  handled:   { label: 'Handled',   cls: 'bg-status-resolved/12 text-status-resolved' },
  dismissed: { label: 'Dismissed', cls: 'bg-paper-muted text-ink-muted' },
};

function FailedEmailsTab({ onCountsChange }) {
  const [rows, setRows]                 = useState([]);
  const [counts, setCounts]             = useState({ open: 0, handled: 0, dismissed: 0 });
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading]           = useState(true);
  const [detail, setDetail]             = useState(null);
  const [note, setNote]                 = useState('');
  const [acting, setActing]             = useState(false);

  const load = async (filter = statusFilter) => {
    setLoading(true);
    try {
      const res = await api.get('/failed-emails', { params: filter ? { status: filter } : {} });
      setRows(res.data.emails || []);
      setCounts(res.data.counts || { open: 0, handled: 0, dismissed: 0 });
      onCountsChange?.(res.data.counts?.open || 0);
    } catch { toast.error('Failed to load failed emails'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(statusFilter); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const act = async (id, status) => {
    setActing(true);
    try {
      await api.patch(`/failed-emails/${id}`, { status, handling_note: note.trim() || undefined });
      toast.success(status === 'handled' ? 'Marked as handled' : 'Dismissed');
      setDetail(null); setNote('');
      load(statusFilter);
    } catch (err) { toast.error(err.response?.data?.error || 'Action failed'); }
    finally { setActing(false); }
  };

  const fmtDT = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
  const truncate = (s, n) => (s || '').length > n ? s.slice(0, n) + '…' : (s || '—');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-base text-ink">Failed Emails</h3>
          <p className="mt-0.5 text-[13px] text-ink-muted">Incoming emails the agent could not process — review and handle manually.</p>
        </div>
        <button onClick={() => load(statusFilter)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-field border border-paper-border2 text-ink-muted hover:bg-paper-muted">
          <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Status filter pills with counts */}
      <div className="flex flex-wrap gap-2">
        {['open', 'handled', 'dismissed', ''].map((s) => (
          <button key={s || 'all'} onClick={() => setStatusFilter(s)}
            className={`rounded-field px-3 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === s
                ? 'bg-ink text-paper'
                : 'border border-paper-border2 bg-paper text-ink-muted hover:bg-paper-muted'
            }`}>
            {s ? `${FAILED_EMAIL_STATUS_CFG[s].label} (${counts[s] ?? 0})` : 'All'}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-card border border-paper-border bg-paper-panel shadow-soft">
        {loading ? (
          <div className="flex justify-center py-12"><ArrowsClockwise size={24} className="animate-spin text-status-under-process" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-ink-faint">No {statusFilter || ''} failed emails 🎉</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-paper-border bg-paper-muted/40">
                  {['Received', 'Sender', 'Subject', 'Error Node', 'Error', 'Status'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-faint">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}
                    onClick={() => { setDetail(r); setNote(''); }}
                    className="cursor-pointer border-b border-paper-border transition-colors hover:bg-paper-muted/50">
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-ink-faint">{fmtDT(r.received_at || r.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-ink">
                      <div>{r.sender_name || '—'}</div>
                      <div className="text-xs text-ink-faint">{r.sender_email || ''}</div>
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-sm text-ink-muted">{r.subject || '—'}</td>
                    <td className="px-4 py-3 text-xs text-ink-muted">{r.error_node || '—'}</td>
                    <td className="max-w-xs truncate px-4 py-3 text-xs text-status-escalated">{truncate(r.error_message, 60)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-chip px-2 py-0.5 text-xs font-medium ${FAILED_EMAIL_STATUS_CFG[r.status]?.cls || 'bg-paper-muted text-ink-muted'}`}>
                        {FAILED_EMAIL_STATUS_CFG[r.status]?.label || r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className={OVERLAY} onClick={() => setDetail(null)}>
          <div className={`${MODAL_CARD} max-h-[85vh] max-w-2xl overflow-y-auto p-6`} onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h4 className="font-serif text-base text-ink">{detail.subject || '(no subject)'}</h4>
                <p className="mt-0.5 text-sm text-ink-muted">
                  {detail.sender_name || detail.sender_email || 'Unknown sender'}
                  {detail.sender_email && detail.sender_name ? ` · ${detail.sender_email}` : ''}
                  {' · '}{fmtDT(detail.received_at || detail.created_at)}
                </p>
              </div>
              <button onClick={() => setDetail(null)} className="text-ink-faint hover:text-ink"><X size={18} /></button>
            </div>

            <div className="flex flex-col gap-4">
              <div className="rounded-field border border-status-escalated/30 bg-status-escalated/8 p-3">
                <div className="mb-1 text-xs font-semibold text-status-escalated">Error {detail.error_node ? `— ${detail.error_node}` : ''}</div>
                <div className="whitespace-pre-wrap break-words text-sm text-status-escalated/90">{detail.error_message || '—'}</div>
                {detail.execution_url && (
                  <a href={detail.execution_url} target="_blank" rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs text-status-under-process hover:underline">
                    <ArrowSquareOut size={12} /> Open n8n execution {detail.execution_id ? `#${detail.execution_id}` : ''}
                  </a>
                )}
              </div>

              <div>
                <div className="mb-1 text-xs font-semibold uppercase text-ink-faint">Email body</div>
                <div className="max-h-64 overflow-y-auto whitespace-pre-wrap break-words rounded-field border border-paper-border bg-paper-muted p-3 text-sm text-ink-muted">
                  {detail.body_text || '(empty)'}
                </div>
              </div>

              {Array.isArray(detail.attachments_meta) && detail.attachments_meta.length > 0 && (
                <div>
                  <div className="mb-1 text-xs font-semibold uppercase text-ink-faint">Attachments (not stored)</div>
                  <ul className="list-inside list-disc text-sm text-ink-muted">
                    {detail.attachments_meta.map((a, i) => (
                      <li key={i}>{a.name || 'file'}{a.size ? ` · ${Math.round(a.size / 1024)} KB` : ''}{a.type ? ` · ${a.type}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}

              {detail.status !== 'open' ? (
                <div className="text-sm text-ink-muted">
                  {FAILED_EMAIL_STATUS_CFG[detail.status]?.label} by {detail.handled_by_name || '—'} at {fmtDT(detail.handled_at)}
                  {detail.handling_note && <div className="mt-1 italic">"{detail.handling_note}"</div>}
                </div>
              ) : (
                <div>
                  <label className="mb-1 block text-xs text-ink-faint">Handling note (optional)</label>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                    className={`${INPUT} resize-none`}
                    placeholder="e.g. Created ticket manually / spam" />
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => act(detail.id, 'handled')} disabled={acting}
                      className="rounded-field bg-status-resolved px-4 py-2 text-sm font-medium text-paper hover:bg-status-resolved/90 disabled:opacity-50">
                      Mark Handled
                    </button>
                    <button onClick={() => act(detail.id, 'dismissed')} disabled={acting}
                      className="rounded-field bg-paper-muted px-4 py-2 text-sm font-medium text-ink-muted hover:bg-paper-border2 disabled:opacity-50">
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main container ────────────────────────────────────────────────────────────

const BASE_TABS = [
  { key: 'overview',      label: 'Overview',      icon: ChartBar,        superAdminOnly: false },
  { key: 'routing',       label: 'Routing Rules', icon: GitBranch,       superAdminOnly: false },
  { key: 'system_mode',   label: 'System Mode',   icon: ToggleLeft,      superAdminOnly: true },
  { key: 'failed_emails', label: 'Failed Emails', icon: EnvelopeSimple,  roles: ['super_admin', 'cs_supervisor', 'dept_manager'] },
];

// Integration name for the TestModeBanner list.
const INTEGRATION_NAMES = {
  abraaj_live_mode:            'Abraaj Water',
  lofat_live_mode:             'Lofat Lifestyle',
  instagram_abraaj_live_mode:  'Instagram · Abraaj',
};

export default function AdminPanel() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [entities, setEntities]   = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [openFailedCount, setOpenFailedCount] = useState(0);
  const [modeData, setModeData]   = useState(null); // hoisted for TestModeBanner

  const TABS = BASE_TABS.filter((t) =>
    (!t.superAdminOnly || user?.role === 'super_admin') &&
    (!t.roles || t.roles.includes(user?.role))
  );
  const canSeeFailedEmails = ['super_admin', 'cs_supervisor', 'dept_manager'].includes(user?.role);

  useEffect(() => {
    Promise.all([
      api.get('/entities'),
      api.get('/tickets/stats'),
    ]).then(([ent, st]) => {
      setEntities(ent.data);
      setStats(st.data);
    }).catch(() => toast.error('Failed to load admin data'))
      .finally(() => setLoading(false));

    if (canSeeFailedEmails) {
      api.get('/failed-emails', { params: { status: 'open', limit: 1 } })
        .then((r) => setOpenFailedCount(r.data.counts?.open || 0))
        .catch(() => {});
    }

    // Hoisted mode-data fetch for the TestModeBanner. SystemModeTab and
    // OverviewTab keep their own fetches for post-toggle refresh — this
    // one only powers the banner.
    fetch('/api/settings/mode')
      .then((r) => r.json())
      .then(setModeData)
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute which integrations are still in test mode. When modeData hasn't
  // loaded, treat the array as empty (banner hides).
  const activeInTestMode = modeData
    ? Object.entries(INTEGRATION_NAMES)
        .filter(([key]) => modeData[key] === false)
        .map(([, label]) => label)
    : [];

  // Build tab labels as JSX (icon + text + optional count badge) so the Tabs
  // primitive can render them directly.
  const availableTabs = TABS.map((tab) => {
    const Icon = tab.icon;
    return {
      key: tab.key,
      label: (
        <span className="flex items-center gap-2">
          <Icon size={14} />
          {tab.label}
          {tab.key === 'failed_emails' && openFailedCount > 0 && (
            <span className="min-w-[20px] rounded-chip bg-status-pending-finance/12 px-1.5 py-0.5 text-center text-xs font-medium text-status-pending-finance">
              {openFailedCount}
            </span>
          )}
        </span>
      ),
    };
  });

  return (
    <AdminPanelView
      activeTab={activeTab}
      availableTabs={availableTabs}
      onTabChange={setActiveTab}
      loading={loading}>
      {activeTab === 'overview'    && <OverviewTab entities={entities} stats={stats} />}
      {activeTab === 'routing'     && <RoutingRulesTab entities={entities} />}
      {activeTab === 'system_mode' && user?.role === 'super_admin' && <SystemModeTab />}
      {activeTab === 'failed_emails' && canSeeFailedEmails && <FailedEmailsTab onCountsChange={setOpenFailedCount} />}
    </AdminPanelView>
  );
}
